import { useEffect, useState, useMemo } from 'react'
import { usePortalContext } from '@stlm/common-ui/lib'
import { DiagonalCoords } from '../constants/types'

const LeipzigName = '__leipzig__'

export const useInitialPosition = () => {
  const { country } = usePortalContext()

  const countryRef = useMemo(() => {
    return CountriesCenters.has(country.code)
      ? CountriesCenters.get(country.code)!
      : Leipzig
  }, [country.code])

  const [position, setPosition] = useState(countryRef)

  useEffect(() => {
    setPosition(countryRef)
  }, [countryRef])
  return position
}

interface Position {
  centerLat: number
  centerLng: number
  zoom: number
  code: string
  bounds: DiagonalCoords
}

const Leipzig = {
  centerLat: 51.34,
  centerLng: 12.372,
  zoom: 13,
  code: LeipzigName,
  bounds: {
    nwLat: 51.38795899266346,
    nwLng: 12.26297378540039,
    seLat: 51.29262643331843,
    seLng: 12.480812072753908,
  },
} as Position

const CountriesCenters = new Map<string, Position>([
  [
    'HR',
    {
      centerLat: 45.1,
      centerLng: 15.2,
      zoom: 7,
      code: 'HR',
      bounds: {
        nwLat: 46.5545,
        nwLng: 13.3832,
        seLat: 42.3856,
        seLng: 19.4275,
      },
    },
  ],
  [
    'GE',
    {
      centerLat: 42.177095,
      centerLng: 43.484977,
      zoom: 8,
      code: 'GE',
      bounds: {
        nwLat: 43.58660173994884,
        nwLng: 40.02424096107483,
        seLat: 40.423448312671015,
        seLng: 46.951713562706854,
      },
    },
  ],
  [
    'PL',
    {
      centerLat: 51.9194,
      centerLng: 19.1451,
      zoom: 7,
      code: 'PL',
      bounds: {
        nwLat: 54.9039,
        nwLng: 14.0746,
        seLat: 49.0027,
        seLng: 24.0295,
      },
    },
  ],
  [
    'ES',
    {
      centerLat: 40.3967643,
      centerLng: -3.7133794,
      zoom: 6,
      code: 'ES',
      bounds: {
        nwLat: 43.84405601552897,
        nwLng: -9.536132812500002,
        seLat: 35.345174516104476,
        seLng: 4.7900390625,
      },
    },
  ],
  [
    'TR',
    {
      centerLat: 38.9597594,
      centerLng: 34.9249653,
      zoom: 6,
      code: 'TR',
      bounds: {
        nwLat: 42.086928667167115,
        nwLng: 25.628662109375,
        seLat: 35.75946584979235,
        seLng: 44.82177734375001,
      },
    },
  ],
  [
    'DE',
    {
      centerLat: 51.1642292,
      centerLng: 10.4541194,
      zoom: 6,
      code: 'DE',
      bounds: {
        nwLat: 56.897003921272606,
        nwLng: -3.5375976562500004,
        seLat: 44.63739123445587,
        seLng: 24.345703125,
      },
    },
  ],
  [
    'LT',
    {
      centerLat: 54.6867397,
      centerLng: 25.2790504,
      zoom: 6,
      code: 'LT',
      bounds: {
        nwLat: 56.78282726360802,
        nwLng: 19.555664062500004,
        seLat: 53.57293832648609,
        seLng: 27.377929687500004,
      },
    },
  ],
  [
    'ME',
    {
      centerLat: 42.7044223,
      centerLng: 19.3957785,
      zoom: 9,
      code: 'ME',
      bounds: {
        nwLat: 43.59431676355149,
        nwLng: 17.644042968750004,
        seLat: 41.79998325207397,
        seLng: 21.129455566406254,
      },
    },
  ],
  [
    'NL',
    {
      centerLat: 52.2129919,
      centerLng: 5.2793703,
      zoom: 8,
      code: 'NL',
      bounds: {
        nwLat: 53.677188271888085,
        nwLng: 1.7907714843750002,
        seLat: 50.68427770577119,
        seLng: 8.761596679687502,
      },
    },
  ],
  [
    'UZ',
    {
      centerLat: 41.381166,
      centerLng: 64.5735819,
      zoom: 6,
      code: 'UZ',
      bounds: {
        nwLat: 48.29781249243716,
        nwLng: 50.55908203125001,
        seLat: 33.651208299204995,
        seLng: 78.4423828125,
      },
    },
  ],
  [
    'CO',
    {
      centerLat: 4.6529539,
      centerLng: -74.0835643,
      zoom: 6,
      code: 'CO',
      bounds: {
        nwLat: -79.68074,
        nwLng: -4.230484,
        seLat: -66.86983,
        seLng: 13.11676,
      },
    },
  ],
  [
    'MX',
    {
      centerLat: 23.6585116,
      centerLng: -102.0077097,
      zoom: 6,
      code: 'MX',
      bounds: {
        nwLat: 14.3886243,
        nwLng: 32.7186679,
        seLat: -118.599188,
        seLng: -86.493266,
      },
    },
  ],
  [
    'KZ',
    {
      centerLat: 48.1012954,
      centerLng: 66.7780818,
      zoom: 6,
      code: 'KZ',
      bounds: {
        nwLat: 40.5686476,
        nwLng: 55.4421701,
        seLat: 46.4932179,
        seLng: 87.3156336,
      },
    },
  ],
  [
    'RS',
    {
      centerLat: 44.024322850000004,
      centerLng: 21.07657433209902,
      zoom: 6,
      code: 'RS',
      bounds: {
        nwLat: 41.8576408,
        nwLng: 46.1902839,
        seLat: 18.8149945,
        seLng: 23.006309,
      },
    },
  ],
  [
    'AM',
    {
      centerLat: 40.0233,
      centerLng: 45.0581,
      zoom: 8,
      code: 'AM',
      bounds: {
        nwLat: 38.738,
        nwLng: 41.30876,
        seLat: 43.296708,
        seLng: 46.819499,
      },
    },
  ],
  [
    'IN',
    {
      centerLat: 21.82913,
      centerLng: 78.0908,
      zoom: 6,
      code: 'IN',
      bounds: {
        nwLat: 6.926426,
        nwLng: 36.73184,
        seLat: 66.621093,
        seLng: 89.560546,
      },
    },
  ],
])
